import React, { useMemo, useState } from "react";
import {
  Card,
  Typography,
  List,
  Space,
  Tooltip,
  Tag,
  Divider,
  Button,
  Spin,
} from "antd";
import {
  InfoCircleOutlined,
  DollarOutlined,
  DownOutlined,
  UpOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import type { BookingQuoteWithComponents } from "../types/bookingQuote";
import { formatEuro } from "./BookingWizard/PricingCalculatorHelpers";

const { Text } = Typography;

interface BookingQuoteDisplayProps {
  quote: BookingQuoteWithComponents;
  loading?: boolean;
}

export const BookingQuoteDisplay: React.FC<BookingQuoteDisplayProps> = ({
  quote,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const groupedComponents = useMemo(() => {
    return quote.components.reduce((groups, component) => {
      const group = component.category_group;
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push(component);
      return groups;
    }, {} as Record<string, typeof quote.components>);
  }, [quote.components]);

  const categoryTitles: Record<string, string> = {
    transport: t(
      "bookingWizard.pricingCalculator.transportCostsSectionTitle",
      { defaultValue: "Transport" }
    ),
    customs: t(
      "bookingWizard.pricingCalculator.customsHandlingSectionTitle",
      { defaultValue: "Customs & Handling" }
    ),
    handling: t(
      "bookingWizard.pricingCalculator.handlingSectionTitle",
      { defaultValue: "Handling & Warehouse" }
    ),
    delivery: t(
      "bookingWizard.pricingCalculator.deliveryCostsSectionTitle",
      { defaultValue: "Delivery" }
    ),
    other: t("bookingWizard.pricingCalculator.otherCosts", { defaultValue: "Other Costs" }),
  };

  const getCalculationTypeLabel = (calculationType: string): string => {
    const labels: Record<string, string> = {
      per_carton: t("pricingCalculator.perCarton", { defaultValue: "per carton" }),
      per_pallet: t("pricingCalculator.perPallet", { defaultValue: "per pallet" }),
      per_unique_product: t("pricingCalculator.perProduct", { defaultValue: "per product" }),
      per_kg: t("pricingCalculator.perKg", { defaultValue: "per kg" }),
      per_cbm: t("pricingCalculator.perCbm", { defaultValue: "per CBM" }),
      per_shipment: t("pricingCalculator.perShipment", { defaultValue: "per shipment" }),
      per_container: t("pricingCalculator.perContainer", { defaultValue: "per container" }),
      fixed: t("pricingCalculator.fixed", { defaultValue: "fixed" }),
      percentage: t("pricingCalculator.percentage", { defaultValue: "percentage" }),
    };
    return labels[calculationType] || calculationType;
  };

  const renderComponent = (component: (typeof quote.components)[0]) => (
    <List.Item
      key={component.id}
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        padding: "8px 0",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <Space direction="vertical" size={2} style={{ width: "100%" }}>
          <Space size={4}>
            <Text strong={!component.is_optional}>{component.component_name}</Text>
            {component.is_optional && !component.is_enabled && (
              <Text type="secondary" style={{ fontSize: 12, fontStyle: "italic" }}>
                {t("bookingQuote.notIncluded", "not included")}
              </Text>
            )}
            {component.component_description && (
              <Tooltip title={component.component_description}>
                <InfoCircleOutlined style={{ color: "#8c8c8c", fontSize: 12 }} />
              </Tooltip>
            )}
          </Space>
          <Space size={4} wrap>
            <Tag color="blue" style={{ fontSize: 11, margin: 0 }}>
              {getCalculationTypeLabel(component.calculation_type)}
            </Tag>
            {component.quantity > 1 && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {formatEuro(component.unit_price)} × {component.quantity}
              </Text>
            )}
          </Space>
        </Space>
      </div>
      <div style={{ minWidth: 100, textAlign: "right" }}>
        <Text strong type={component.is_enabled ? undefined : "secondary"}>
          {component.is_enabled ? formatEuro(component.total_price) : formatEuro(0)}
        </Text>
      </div>
    </List.Item>
  );

  const renderCategoryGroup = (
    category: string,
    categoryComponents: typeof quote.components
  ) => {
    if (categoryComponents.length === 0) return null;
    const enabledTotal = categoryComponents
      .filter((c) => c.is_enabled)
      .reduce((sum, c) => sum + c.total_price, 0);

    return (
      <div key={category} style={{ marginBottom: 12 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 4,
          }}
        >
          <Text strong>{categoryTitles[category] || category}</Text>
          {enabledTotal > 0 && (
            <Text type="secondary" style={{ fontSize: 13 }}>
              {formatEuro(enabledTotal)}
            </Text>
          )}
        </div>
        <List
          size="small"
          itemLayout="horizontal"
          dataSource={categoryComponents}
          renderItem={renderComponent}
        />
      </div>
    );
  };

  const categoryOrder: Array<
    "transport" | "customs" | "handling" | "delivery" | "other"
  > = ["transport", "customs", "handling", "delivery", "other"];

  const renderExpandedContent = () => {
    if (loading) {
      return (
        <div style={{ padding: 24, textAlign: "center" }}>
          <Spin />
        </div>
      );
    }

    if (!quote.components.length) {
      return (
        <div style={{ padding: 16, textAlign: "center" }}>
          <Text type="secondary">
            {t("bookingQuote.noComponents", "No pricing components available")}
          </Text>
        </div>
      );
    }

    return (
      <>
        <div style={{ marginBottom: 12 }}>
          <Space direction="vertical" size={2}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t("bookingQuote.generatedAt", "Generated")}: {" "}
              {dayjs(quote.quote_generated_at).format("DD-MM-YYYY HH:mm")}
            </Text>
            {quote.matched_template_name && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t("bookingQuote.templateMatched", "Pricing Template")}: {" "}
                {quote.matched_template_name}
              </Text>
            )}
          </Space>
        </div>

        <Divider style={{ margin: "12px 0" }} />

        {categoryOrder.map((category) => {
          const categoryComponents = groupedComponents[category] || [];
          return renderCategoryGroup(category, categoryComponents);
        })}

        <Divider style={{ margin: "12px 0" }} />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 16px",
            background: "#f5f5f5",
            borderRadius: 8,
            border: "1px solid #d9d9d9",
          }}
        >
          <Text strong style={{ fontSize: 15 }}>
            {t("bookingWizard.pricingCalculator.total", "Total Estimated Cost")}
          </Text>
          <Text
            strong
            style={{
              color: "#1890ff",
              fontSize: 18,
            }}
          >
            {formatEuro(quote.total_cost)}
          </Text>
        </div>

        <div
          style={{
            marginTop: 12,
            padding: 10,
            background: "rgba(250, 140, 22, 0.08)",
            borderRadius: 8,
            border: "1px solid rgba(250, 140, 22, 0.3)",
          }}
        >
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t(
              "bookingQuote.snapshotNote",
              "Snapshot saved at submission. Import duties and VAT are excluded and handled separately."
            )}
          </Text>
        </div>
      </>
    );
  };

  return (
    <Card
      style={{ marginBottom: 24, width: "100%" }}
      bodyStyle={{ padding: expanded ? 16 : 12 }}
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
          }}
        >
          <Space size={6} align="center">
            <DollarOutlined style={{ fontSize: 16 }} />
            <Text style={{ fontSize: 16, fontWeight: 600 }}>
              {t("bookingQuote.title", "Price Quote")}
            </Text>
          </Space>
          <Space size={10} align="center">
            <Text strong style={{ fontSize: 16 }}>
              {formatEuro(quote.total_cost)}
            </Text>
            <Button
              type="link"
              size="small"
              onClick={() => setExpanded((prev) => !prev)}
              icon={expanded ? <UpOutlined /> : <DownOutlined />}
            >
              {expanded
                ? t("bookingQuote.hideDetails", "Hide details")
                : t("bookingQuote.showDetails", "Show details")}
            </Button>
          </Space>
        </div>
      }
    >
      {expanded ? renderExpandedContent() : null}
    </Card>
  );
}

